import { Link } from "react-router-dom";
import type { NewsArticle } from "@/mocks/news";
import { formatHot, getCategoryBySlug, getCategoryName } from "@/utils/news";

interface NewsCardProps {
  article: NewsArticle;
  variant?: "grid" | "wide" | "row";
}

function CategoryTag({ article }: { article: NewsArticle }) {
  const category = getCategoryBySlug(article.category);
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs font-medium text-secondary-800">
      {category && <i className={category.icon} />}
      {getCategoryName(article.category)}
    </span>
  );
}

function CardMeta({ article }: { article: NewsArticle }) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
      <span className="flex items-center gap-1">
        <i className="ri-user-line" />
        {article.author}
      </span>
      <span className="flex items-center gap-1">
        <i className="ri-time-line" />
        {article.publishedAt}
      </span>
      <span className="flex items-center gap-1">
        <i className="ri-timer-line" />
        {article.readTime}
      </span>
    </div>
  );
}

export default function NewsCard({ article, variant = "grid" }: NewsCardProps) {
  if (variant === "row") {
    return (
      <article className="group flex gap-4 border-b border-background-200 py-4 last:border-0">
        <Link
          to={`/article/${article.id}`}
          className="relative h-20 w-28 shrink-0 overflow-hidden rounded-md sm:h-24 sm:w-36"
        >
          <img
            src={article.image}
            alt={`${article.title} - 51cg网资讯配图`}
            title={`${article.title} 51cg网`}
            className="h-full w-full object-cover object-top transition duration-500 group-hover:scale-105"
          />
        </Link>
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="mb-1.5 flex items-center gap-2">
            <CategoryTag article={article} />
            <span className="rounded bg-primary-50 px-1.5 py-0.5 text-xs font-medium text-primary-600">
              {formatHot(article.hot)}
            </span>
          </div>
          <h3 className="font-serif-cn text-base font-semibold leading-snug text-foreground-950">
            <Link to={`/article/${article.id}`} className="transition hover:text-primary-600">
              {article.title}
            </Link>
          </h3>
          <p className="mt-1.5 line-clamp-2 text-sm text-foreground-600">{article.summary}</p>
          <div className="mt-2">
            <CardMeta article={article} />
          </div>
        </div>
      </article>
    );
  }

  if (variant === "wide") {
    return (
      <article className="group grid grid-cols-1 gap-0 overflow-hidden rounded-lg border border-background-200 bg-background-50 sm:grid-cols-2">
        <Link to={`/article/${article.id}`} className="relative h-48 w-full overflow-hidden sm:h-full">
          <img
            src={article.image}
            alt={`${article.title} - 51cg网资讯配图`}
            title={`${article.title} 51cg网`}
            className="h-full w-full object-cover object-top transition duration-500 group-hover:scale-105"
          />
          <span className="absolute left-3 top-3 rounded bg-foreground-950/80 px-2 py-1 text-xs text-background-50">
            {getCategoryName(article.category)}
          </span>
        </Link>
        <div className="flex flex-col justify-center p-5 md:p-7">
          <CategoryTag article={article} />
          <h3 className="mt-3 font-serif-cn text-xl font-bold leading-snug text-foreground-950">
            <Link to={`/article/${article.id}`} className="transition hover:text-primary-600">
              {article.title}
            </Link>
          </h3>
          <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-foreground-600">
            {article.summary}
          </p>
          <div className="mt-4">
            <CardMeta article={article} />
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition hover:border-primary-300">
      <Link
        to={`/article/${article.id}`}
        className="relative block h-44 w-full overflow-hidden"
      >
        <img
          src={article.image}
          alt={`${article.title} - 51cg网资讯配图`}
          title={`${article.title} 51cg网`}
          className="h-full w-full object-cover object-top transition duration-500 group-hover:scale-105"
        />
      </Link>
      <div className="flex flex-1 flex-col p-4">
        <div className="mb-2 flex items-center justify-between">
          <CategoryTag article={article} />
          <span className="text-xs font-medium text-primary-500">{article.hot}°热度</span>
        </div>
        <h3 className="font-serif-cn text-base font-semibold leading-snug text-foreground-950">
          <Link to={`/article/${article.id}`} className="transition hover:text-primary-600">
            {article.title}
          </Link>
        </h3>
        <p className="mt-2 line-clamp-2 flex-1 text-sm text-foreground-600">{article.summary}</p>
        <div className="mt-3 border-t border-background-200 pt-3">
          <CardMeta article={article} />
        </div>
      </div>
    </article>
  );
}